import type { TrackEvent, TrackPlugin } from './types';
import type { SafeShell } from './safe-shell';

/**
 * 埋点事件流转管道
 */
export class Pipeline {
  private plugins: TrackPlugin[] = [];

  constructor(private safeShell: SafeShell) {}

  public use(plugin: TrackPlugin): void {
    this.plugins.push(plugin);
  }

  public hasSendHandler(): boolean {
    return this.plugins.some((plugin) => typeof plugin.send === 'function');
  }

  public runBeforeProduce(eventName: string, properties: Record<string, any>): boolean {
    for (const plugin of this.plugins) {
      if (!plugin.beforeProduce) continue;
      const shouldContinue = this.safeShell.run(
        () => plugin.beforeProduce!(eventName, properties),
        true,
        `Plugin[${plugin.name}].beforeProduce`
      );
      if (shouldContinue === false) {
        return false;
      }
    }
    return true;
  }

  public runTransform(event: TrackEvent): TrackEvent | null {
    let currentEvent: TrackEvent | null = event;

    for (const plugin of this.plugins) {
      if (!plugin.transform || !currentEvent) continue;
      currentEvent = this.safeShell.run(
        () => plugin.transform!(currentEvent!),
        currentEvent,
        `Plugin[${plugin.name}].transform`
      );
    }

    return currentEvent;
  }

  public runBeforeSend(event: TrackEvent): boolean {
    for (const plugin of this.plugins) {
      if (!plugin.beforeSend) continue;
      const res = this.safeShell.run(
        () => plugin.beforeSend!(event),
        true,
        `Plugin[${plugin.name}].beforeSend`
      );
      if (res === false) {
        return false;
      }
    }
    return true;
  }

  public async runSend(event: TrackEvent): Promise<boolean> {
    for (const plugin of this.plugins) {
      if (!plugin.send) continue;
      const handled = await this.safeShell.runAsync(
        async () => (await plugin.send!(event)) ?? false,
        false,
        `Plugin[${plugin.name}].send`
      );
      if (handled) return true;
    }
    return false;
  }

  public runAfterSend(event: TrackEvent, success: boolean): void {
    for (const plugin of this.plugins) {
      if (!plugin.afterSend) continue;
      this.safeShell.run(
        () => plugin.afterSend!(event, success),
        undefined,
        `Plugin[${plugin.name}].afterSend`
      );
    }
  }

  public destroy(): void {
    for (const plugin of [...this.plugins].reverse()) {
      if (!plugin.destroy) continue;
      this.safeShell.run(
        () => plugin.destroy!(),
        undefined,
        `Plugin[${plugin.name}].destroy`
      );
    }
    this.plugins = [];
  }
}
